/**
 * The browser half of presence: one socket per page, and a store to read it.
 *
 * **Owned by website-main.** Copied into every SPA that draws a person —
 * change it here and run `node scripts/sync-shared.mjs` from the workspace
 * root. `shared/copies.test.ts` catches an edit in place; `--check` is the only
 * thing that catches this file moving and a copy being missed.
 *
 * Browser-only. It touches `WebSocket`, `document` and `navigator`, and is
 * imported by a site's state provider, not by a Worker — the same shape as
 * `apply-branding.ts` beside it, and for the same reason: this is logic every
 * site needs identically, not a component every site draws differently.
 *
 * ── Same origin, always ─────────────────────────────────────────────────────
 *
 * The socket goes to **this page's own origin**, and each site's Worker
 * forwards it to website-main. That is not indirection for its own sake.
 * Every SPA in the suite sets `connect-src 'self'`, and website-cloud's
 * `worker/lib/headers.ts` spends a page of argument on why exactly one foreign
 * origin was ever admitted there — on the page that holds the vault master key
 * in memory. A live, cookie-carrying socket is precisely the shape that
 * argument exists to exclude, and widening four separate CSPs to admit it would
 * mean making that argument four more times, for a status dot.
 *
 * The precedent is already in the codebase: analytics batches are taken
 * same-origin by each site's Worker and forwarded with a shared token, for this
 * exact reason.
 *
 * ── Why the client derives, and does not just render what it is told ────────
 *
 * `PresenceDO` has no alarm, deliberately: a per-user timer is a wake-up per
 * user per interval whether or not anything happened. So nothing on the server
 * notices somebody going quiet — the frame carries `lastSeenAt` and
 * `connections` as well as the answer, and this file runs `effectivePresence()`
 * on its own clock between frames.
 *
 * That is why going idle turns the dot amber five minutes later with no network
 * traffic at all, and why coming back sends exactly one message.
 *
 * ── The heartbeat is free and the beat is not ───────────────────────────────
 *
 * Two different things, and confusing them is what would make this expensive:
 *
 *   `ping`  every 30s, answered by the runtime's `setWebSocketAutoResponse`
 *           **without waking the object**. It proves the socket is alive. It
 *           does not prove anybody is there, and it must not — a tab left open
 *           all day would otherwise report "available" all day.
 *
 *   `beat`  sent only when somebody actually touched the keyboard or the
 *           pointer, and at most once a minute. This one wakes the object,
 *           because it is the one that means something.
 */

import {
  effectivePresence,
  isSettablePresence,
  type Presence,
  type PresenceState
} from './presence';

/** How often the socket proves it is alive. Matches `HEARTBEAT_SECONDS` in
 *  `worker/presence.ts`, which is where the runtime's auto-response is set. */
const PING_MS = 30_000;

/** At most one "somebody is here" message a minute, however much they type. */
const BEAT_EVERY_MS = 60_000;

/** How often the derived answer is recomputed locally. Nothing leaves the
 *  machine; this is what makes the five-minute window arrive on time. */
const TICK_MS = 30_000;

/** Backoff between reconnection attempts, in milliseconds. The last is
 *  repeated for as long as it takes — a socket that cannot reconnect is a
 *  laptop in a tunnel far more often than it is a bug. */
const BACKOFF_MS = [1_000, 2_000, 5_000, 15_000, 30_000, 60_000];

// ── the store ───────────────────────────────────────────────────────────────

/**
 * The last thing the server said, and when it said it.
 *
 * `null` until the first answer arrives — which is not the same as `unknown`,
 * and the difference is visible: `unknown` draws no dot, so a page that has not
 * asked yet and a page whose answer is "we do not know" look identical, which
 * is correct.
 */
let state: PresenceState | null = null;

/** What `getPresence()` returns. Derived from `state` on this machine's clock,
 *  recomputed on a tick, and a plain string — so it is referentially stable and
 *  `useSyncExternalStore` has nothing to loop on. */
let derived: Presence = 'unknown';

const listeners = new Set<() => void>();

/** The presence to draw, right now. */
export function getPresence(): Presence {
  return derived;
}

/**
 * What this person chose, or null if they have not chosen.
 *
 * The picker needs this rather than `getPresence()`: somebody who chose
 * "available" and then went to lunch is shown as away, and a tick against
 * "Away" in the menu would be telling them they chose something they did not.
 */
export function getPresenceOverride(): Presence | null {
  return state?.override ?? null;
}

/** Subscribe to changes. Pairs with either getter under `useSyncExternalStore`. */
export function subscribePresence(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function publish(force = false): void {
  const next = state ? effectivePresence({ ...state, now: Date.now() }) : 'unknown';
  if (next === derived && !force) return;
  derived = next;
  for (const fn of listeners) fn();
}

/** Take a frame, or an HTTP body, and redraw if it changed anything. */
function accept(next: PresenceState): void {
  const before = state;
  state = next;
  // The override is read by the picker's tick but is not part of the derived
  // answer, so a change to it alone would not survive the comparison above.
  // Somebody clearing "do not disturb" while still at their desk is exactly
  // that case: the dot stays red, the tick has to move.
  publish(before?.override !== next.override);
}

// ── the socket ──────────────────────────────────────────────────────────────

let socket: WebSocket | null = null;
let attempt = 0;
let running = false;
let base = '/api/presence';
let tag = 'web';

let pingTimer: ReturnType<typeof setInterval> | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let lastBeatAt = 0;

function socketUrl(): string {
  const url = new URL(`${base}/socket`, window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('app', tag);
  return url.toString();
}

function open(): void {
  if (!running || socket) return;

  let ws: WebSocket;
  try {
    ws = new WebSocket(socketUrl());
  } catch {
    // A malformed URL, or a browser refusing the scheme. Retrying will not
    // help, but the backoff means it costs almost nothing to keep trying, and
    // giving up permanently on the first load of a page served over `file:`
    // would be worse.
    retry();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    attempt = 0;
    // The object writes `lastSeenAt` when a socket connects, so opening one is
    // itself a sign of life and needs no beat.
    lastBeatAt = Date.now();
  };

  ws.onmessage = (event) => {
    // `pong`, from the runtime's auto-response. It never reached the object and
    // it is not JSON.
    if (typeof event.data !== 'string' || event.data === 'pong') return;
    let frame: unknown;
    try {
      frame = JSON.parse(event.data);
    } catch {
      return;
    }
    if (isState(frame)) accept(frame);
  };

  ws.onclose = () => {
    if (socket === ws) socket = null;
    // Nothing is claimed about presence here. The object announces to everybody
    // else that a tab went; for this tab, the last thing it was told stays on
    // screen until the socket comes back or the derivation ages it out — which
    // it will, because `connections` in the last frame is now a lie in exactly
    // the direction that decays.
    retry();
  };

  ws.onerror = () => {
    // `close` follows. Handling it here as well would double the backoff.
  };
}

function retry(): void {
  if (!running || retryTimer) return;
  const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
  attempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    open();
  }, wait);
}

function isState(value: unknown): value is PresenceState {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.presence === 'string' &&
    typeof v.connections === 'number' &&
    (v.override === null || isSettablePresence(v.override)) &&
    (v.lastSeenAt === null || typeof v.lastSeenAt === 'number')
  );
}

/** Somebody is here. Throttled — see the header for why this is not the ping. */
function beat(): void {
  if (!running) return;
  const now = Date.now();
  if (now - lastBeatAt < BEAT_EVERY_MS) return;
  lastBeatAt = now;
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ t: 'beat' }));
}

const onInput = (): void => beat();

const onVisible = (): void => {
  if (document.visibilityState !== 'visible') return;
  // Coming back to a tab is the clearest "I am here" there is, and a laptop
  // that slept has a socket that thinks it is open. Ask for both.
  beat();
  if (!socket || socket.readyState === WebSocket.CLOSED) {
    attempt = 0;
    open();
  }
};

const onOnline = (): void => {
  attempt = 0;
  if (!socket) open();
};

// ── the lifecycle ───────────────────────────────────────────────────────────

/**
 * Start listening, once the viewer is known to be signed in.
 *
 * Idempotent: calling it again with the same options does nothing, which
 * matters because the natural call site is an effect that re-runs whenever
 * `/me` refreshes.
 *
 * `path` is this site's own proxy prefix — `/api/presence` on website-main
 * itself, and whatever each SPA's Worker mounts elsewhere. `app` tags the
 * socket so a later feature can say *where* somebody is; nothing reads it yet.
 */
export function startPresence(options: { app: string; path?: string } = { app: 'web' }): void {
  if (typeof window === 'undefined') return;

  const nextBase = (options.path ?? '/api/presence').replace(/\/+$/, '');
  if (running && nextBase === base && options.app === tag) return;
  if (running) stopPresence();

  base = nextBase;
  tag = options.app;
  running = true;
  attempt = 0;

  // One HTTP read before the socket, for two reasons. It fills the dot on the
  // first paint rather than on the first frame; and a deployment that has not
  // got the routes yet answers 404 here, so this page stays at `unknown` and
  // draws nothing instead of reconnecting a socket forever.
  void fetch(`${base}`, { credentials: 'same-origin', headers: { accept: 'application/json' } })
    .then(async (res) => {
      if (!running) return;
      if (!res.ok) {
        running = false;
        return;
      }
      const body: unknown = await res.json();
      if (isState(body)) accept(body);
      open();
    })
    .catch(() => {
      // A network failure, rather than an answer. The feature may well be
      // there; try the socket, which has its own backoff.
      if (running) open();
    });

  pingTimer = setInterval(() => {
    if (socket?.readyState === WebSocket.OPEN) socket.send('ping');
  }, PING_MS);
  tickTimer = setInterval(publish, TICK_MS);

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);
  // `passive`, and only the three that mean a person: a scroll is a mouse
  // wheel resting on a desk as often as it is somebody reading.
  window.addEventListener('pointerdown', onInput, { passive: true });
  window.addEventListener('keydown', onInput, { passive: true });
  window.addEventListener('focus', onInput);
}

/** Stop, and forget. Call on sign-out — the next person at this browser is not
 *  this person, and a socket that outlived the session would say they were. */
export function stopPresence(): void {
  running = false;

  if (pingTimer) clearInterval(pingTimer);
  if (tickTimer) clearInterval(tickTimer);
  if (retryTimer) clearTimeout(retryTimer);
  pingTimer = tickTimer = null;
  retryTimer = null;

  if (typeof window !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('pointerdown', onInput);
    window.removeEventListener('keydown', onInput);
    window.removeEventListener('focus', onInput);
  }

  const ws = socket;
  socket = null;
  ws?.close();

  state = null;
  derived = 'unknown';
  for (const fn of listeners) fn();
}

/**
 * Choose a presence, or clear the choice.
 *
 * Over the socket when there is one, because that is one hop rather than three
 * and the object fans it out to every other tab either way. The `PUT` is the
 * fallback for the moment before the socket opens — and for the desktop apps,
 * which have no socket at all.
 *
 * The answer is not awaited from the socket path: the object announces the new
 * state to every open tab including this one, so the dot changes when the
 * change is real rather than when this function returns.
 */
export async function setPresence(presence: Presence | null): Promise<void> {
  if (presence !== null && !isSettablePresence(presence)) return;

  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(presence === null ? { t: 'clear' } : { t: 'set', presence }));
    return;
  }

  const res = await fetch(base, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ presence })
  });
  if (!res.ok) return;
  const body: unknown = await res.json();
  if (isState(body)) accept(body);
}
